import { ErrorMessage, Field } from "formik";
import { IMAGE_FETCH_URL, IMAGE_URL } from "../../config";
import cameraIcon from "../../assets/icons/camera.svg";
import get from "lodash/get";
import { useState, useRef, useEffect } from "react";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormImageField = ({
  label = "imageField",
  labelClassName = "",
  name = "imageField",
  className = "",
  previewClassName = "",
  camera = false,
  maxSize = 5, // Default to 5MB
  accept = "image/*",
  required = false,
  ...props
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const uploadFile = (file, form, name) => {
    if (!file) return;

    // --- Dynamic Size Validation ---
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      alert(`File is too large! Maximum size allowed is ${maxSize}MB.`);
      resetInput();
      return;
    }

    // Revoke previous blob preview if present
    if (previewUrl) URL.revokeObjectURL(previewUrl);

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText);
          const filePath = response?.data?.filePath;

          if (!filePath) {
            throw new Error("Invalid response schema");
          }

          const fullUrl = filePath.startsWith("http")
            ? filePath
            : `${IMAGE_FETCH_URL}${filePath}`;

          // Formik field updates
          form.setFieldValue(name, fullUrl);
          form.setFieldTouched(name, true, false);
        } catch (e) {
          alert("Failed to process image response!");
        }
      } else {
        alert("Image upload failed!");
      }
      resetInput();
    };

    xhr.onerror = () => {
      setIsUploading(false);
      alert("An error occurred during upload!");
      resetInput();
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files?.[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer?.files?.[0];
    uploadFile(file, form, name);
  };

  const handleRemoveImage = (form, name) => {
    form.setFieldValue(name, "");
    form.setFieldTouched(name, true, false);
    resetInput();

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const imageUrl = get(form.values, name);
        const hasError = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-1.5 w-full ${className}`}>
            {label && (
              <label
                htmlFor={name}
                className={`text-fs_14 md:text-fs_16 font-medium capitalize select-none transition-colors ${
                  hasError
                    ? "text-errorColor"
                    : labelClassName || "text-darkText"
                }`}
              >
                {formatUnderscoreToSpace(label)}
                {required && <span className="text-errorColor ml-0.5">*</span>}
              </label>
            )}

            {!imageUrl ? (
              <div
                className={`border-2 border-dashed rounded-xl p-4 transition-all w-[180px] h-[180px] relative overflow-hidden cursor-pointer flex flex-col items-center justify-center bg-gray-50/50 ${
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : hasError
                      ? "border-errorColor bg-red-50/30"
                      : "border-slate-300 hover:border-primary hover:bg-slate-50"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
                onClick={() => inputRef.current?.click()}
              >
                {/* Local Preview Image */}
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Uploading preview"
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                  />
                )}

                {/* Upload Overlay */}
                {isUploading ? (
                  <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-xs flex flex-col items-center justify-center text-white z-10 px-4">
                    <p className="text-xs font-semibold mb-2 truncate max-w-full">
                      Uploading...
                    </p>

                    <div className="w-full bg-white/30 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-400 transition-all duration-150"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>

                    <p className="text-xs font-mono mt-2">{uploadProgress}%</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center z-10 text-center gap-2 p-2">
                    <div className="p-3 bg-white rounded-full shadow-xs border border-slate-100">
                      <img
                        src={cameraIcon}
                        alt="Camera"
                        width={24}
                        height={24}
                        className="w-6 h-6 opacity-75"
                      />
                    </div>
                    <p className="text-slate-600 text-xs">
                      Drag & drop or{" "}
                      <span className="text-primary font-semibold underline underline-offset-2">
                        Browse
                      </span>
                    </p>
                  </div>
                )}

                <input
                  id={name}
                  ref={inputRef}
                  name={name}
                  type="file"
                  className="hidden"
                  accept={accept}
                  capture={camera ? "environment" : undefined}
                  onChange={(e) => handleFileSelect(e, form, name)}
                />
              </div>
            ) : (
              <div className="w-[180px] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs relative group">
                <div className="relative w-full h-[180px]">
                  <img
                    src={imageUrl}
                    alt="Uploaded preview"
                    className={`w-full h-full object-cover ${previewClassName}`}
                  />
                  <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(form, name)}
                      className="text-xs bg-red-500 hover:bg-red-600 text-white font-medium px-3 py-1.5 rounded-md shadow-sm transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="text-errorColor text-fs_14 ps-1 min-h-[1.25rem] flex items-center">
              <ErrorMessage name={name} />
            </div>
          </div>
        );
      }}
    </Field>
  );
};
